import { useContext, useState, useEffect, useCallback } from 'react';
import DashboardContext from '@/contexts/DashboardContext';
import NotificationService from '@/domain/Notifications/NotificationService';
import type { Kind, AlertCardType } from '@/domain/Notifications/Notifications';
import type { TabKey, TabState } from '../types';
import { TAB_KEYS, mapTabToKind } from '../constants';

const notificationService = new NotificationService();

const makeInit = (): TabState => ({ page: 1, items: [], hasMore: true, loading: false });

export function useNotifications() {
	const { value: context, setValue } = useContext(DashboardContext);
	const [activeTab, setActiveTab] = useState<TabKey>(TAB_KEYS.ALL);
	const [showConfirmDismiss, setShowConfirmDismiss] = useState(false);
	const [selectedNotification, setSelectedNotification] = useState<string | null>(null);

	const [byTab, setByTab] = useState<Record<Kind, TabState>>({
		ALL: makeInit(),
		ISSUES: makeInit(),
		RECOMMENDATIONS: makeInit(),
	});

	const currentKind = mapTabToKind(activeTab);
	const currentState = byTab[currentKind];
	const locationId = context.selectedLocation;

	const [tabCounts, setTabCounts] = useState<Record<TabKey, number | undefined>>({
		[TAB_KEYS.ALL]: undefined,
		[TAB_KEYS.SYSTEM]: undefined,
		[TAB_KEYS.FORECAST]: undefined,
	});

	// Refresh all tab counts atomically and sync bell icon badge via context
	const refreshAllTabCounts = useCallback(async () => {
		if (!locationId) return;
		try {
			const [all, issues, recommendations] = await Promise.all([
				notificationService.getRecentNotifications({ locationId, page: 1, kind: 'ALL' }),
				notificationService.getRecentNotifications({ locationId, page: 1, kind: 'ISSUES' }),
				notificationService.getRecentNotifications({ locationId, page: 1, kind: 'RECOMMENDATIONS' }),
			]);

			const allCount = all?.totalRecords ?? 0;
			setTabCounts({
				[TAB_KEYS.ALL]: allCount,
				[TAB_KEYS.SYSTEM]: issues?.totalRecords ?? 0,
				[TAB_KEYS.FORECAST]: recommendations?.totalRecords ?? 0,
			});

			// Sync bell icon badge in header
			setValue((prev) => ({ ...prev, notificationCount: allCount }));
		} catch (error) {
			console.error('Error loading tab counts:', error);
		}
	}, [locationId]);

	// Reset all tabs when location changes
	useEffect(() => {
		setByTab({
			ALL: makeInit(),
			ISSUES: makeInit(),
			RECOMMENDATIONS: makeInit(),
		});
	}, [locationId]);

	// Load data for current tab when it changes
	useEffect(() => {
		const kind = mapTabToKind(activeTab);
		const state = byTab[kind];

		if (!state.loading && state.items.length === 0 && state.hasMore && locationId) {
			loadMore();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab, locationId]);

	// Update counts for all OTHER tabs when switching
	useEffect(() => {
		if (!locationId) return;

		const cKind = mapTabToKind(activeTab);
		const otherKinds: Kind[] = (['ALL', 'ISSUES', 'RECOMMENDATIONS'] as Kind[]).filter((k) => k !== cKind);

		Promise.all(otherKinds.map((kind) => notificationService.getRecentNotifications({ locationId, page: 1, kind })))
			.then((results) => {
				const newCounts: Partial<Record<TabKey, number>> = {};
				otherKinds.forEach((kind, index) => {
					let tabKey: TabKey;
					if (kind === 'ISSUES') {
						tabKey = TAB_KEYS.SYSTEM;
					} else if (kind === 'RECOMMENDATIONS') {
						tabKey = TAB_KEYS.FORECAST;
					} else {
						tabKey = TAB_KEYS.ALL;
					}
					newCounts[tabKey] = results[index]?.totalRecords ?? 0;
				});

				setTabCounts((prev) => ({ ...prev, ...newCounts }));

				// Always sync bell icon with ALL count
				if (newCounts[TAB_KEYS.ALL] !== undefined) {
					setValue((prev) => ({ ...prev, notificationCount: newCounts[TAB_KEYS.ALL] }));
				}
			})
			.catch(() => {});
	}, [activeTab, locationId, setValue]);

	const loadMore = useCallback(async () => {
		const kind = currentKind;
		const snapshot = byTab[kind];
		if (snapshot.loading || !snapshot.hasMore || !locationId) return;

		setByTab((prev) => ({ ...prev, [kind]: { ...prev[kind], loading: true } }));

		try {
			const res = await notificationService.getRecentNotifications({
				locationId,
				page: snapshot.page,
				kind,
			});

			const items = [...(res?.data ?? [])] as AlertCardType[];
			const nextPage = snapshot.page + 1;
			const hasMoreByLength = items.length === (res?.size ?? 10);
			const hasMoreByTotal = res?.totalPages ? nextPage <= res.totalPages : true;

			setTabCounts((prev) => ({
				...prev,
				[kind]: res?.totalRecords ?? 0,
			}));

			// Sync bell icon badge
			if (kind === 'ALL') {
				setValue((prev) => ({ ...prev, notificationCount: res?.totalRecords ?? 0 }));
			} else {
				notificationService
					.getRecentNotifications({ locationId, page: 1, kind: 'ALL' })
					.then((allRes) => {
						setValue((prev) => ({ ...prev, notificationCount: allRes?.totalRecords ?? 0 }));
					})
					.catch(() => {});
			}

			setByTab((prev) => ({
				...prev,
				[kind]: {
					page: nextPage,
					items: [...prev[kind].items, ...items],
					hasMore: hasMoreByLength && hasMoreByTotal,
					loading: false,
				},
			}));
		} catch (error) {
			console.error('Error loading notifications:', error);
			setByTab((prev) => ({ ...prev, [kind]: { ...prev[kind], loading: false } }));
		}
	}, [byTab, currentKind, locationId]);

	const dismissNotification = async (id: string) => {
		const ok = await notificationService.dismissRecentNotifications(id);
		if (!ok) return;

		setByTab({
			ALL: makeInit(),
			ISSUES: makeInit(),
			RECOMMENDATIONS: makeInit(),
		});

		await refreshAllTabCounts();
	};

	// Load all tab counts when location changes
	useEffect(() => {
		refreshAllTabCounts();
	}, [refreshAllTabCounts]);

	return {
		context,
		setValue,
		activeTab,
		setActiveTab,
		tabCounts,
		currentState,
		byTab,
		loadMore,
		dismissNotification,
		showConfirmDismiss,
		setShowConfirmDismiss,
		selectedNotification,
		setSelectedNotification,
	};
}
